import { useMemo } from 'react';
import { Table, TableBody, TableHead, TableRow } from '@mui/material';
import {
  CustomAutoComplete,
  GeneralTableBox,
  HeadTableCell,
  NoDataSkeleton,
  renderTwoLineOption,
  RowTableCell,
  TableBodySkeleton,
} from '../../../common/components';
import { AUTOCOMPLETE_LISTBOX_PROPS } from '../../../common/styles';
import {
  ALL_WAREHOUSES_FILTER,
  ARTICLE_MOVEMENTS_TABLE_TEXTS as TEXTS,
} from '../constants/inventoryArticlesConstants';
import {
  formatMovementDate,
  formatMovementType,
  formatStockQuantity,
  getWarehouseLabel,
} from '../utils/inventoryArticlesUtils';

import ClientPaginatedTable from '../../../common/components/table/ClientPaginatedTable';
const numberSx = { textAlign: 'right' };
const textSx = { textAlign: 'left' };

const COLUMNS = [
  { key: 'date', label: TEXTS.date, sx: { ...textSx, width: 160 } },
  { key: 'warehouse', label: TEXTS.warehouse, sx: { ...textSx, width: 180 } },
  { key: 'type', label: TEXTS.type, sx: { ...textSx, width: 180 } },
  { key: 'quantity', label: TEXTS.quantity, align: 'right', sx: { ...numberSx, width: 120 } },
  { key: 'previous', label: TEXTS.previous, align: 'right', sx: { ...numberSx, width: 140 } },
  { key: 'next', label: TEXTS.next, align: 'right', sx: { ...numberSx, width: 130 } },
  { key: 'reason', label: TEXTS.reason, sx: textSx },
];

/**
 * Historial de movimientos de stock del artículo, con filtro por depósito.
 * @param {Object} props
 * @param {Array<Object>} props.movements Movimientos ya ordenados.
 * @param {Object} props.warehousesByUuid Depósitos indexados por uuid.
 * @param {string} props.warehouseFilter Depósito elegido o `ALL_WAREHOUSES_FILTER`.
 * @param {Array<{value: string, label: string}>} props.warehouseOptions Depósitos con movimientos.
 * @param {Function} props.onWarehouseFilterChange Recibe el depósito elegido.
 * @param {boolean} props.isLoading Carga en curso.
 * @returns {JSX.Element}
 */
const ArticleStockMovementsTable = ({
  movements = [],
  warehousesByUuid = {},
  warehouseFilter,
  warehouseOptions = [],
  onWarehouseFilterChange,
  isLoading = false,
}) => {
  const filterOptions = useMemo(
    () => [{ value: ALL_WAREHOUSES_FILTER, label: TEXTS.allWarehouses }, ...warehouseOptions],
    [warehouseOptions]
  );

  return (
    <>
      <CustomAutoComplete
        label={TEXTS.warehouseFilter}
        name="warehouseFilter"
        value={warehouseFilter}
        options={filterOptions}
        renderOption={renderTwoLineOption}
        inputPlaceholder={TEXTS.warehouseFilterPlaceholder}
        ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
        onChange={(event) => onWarehouseFilterChange?.(event.target.value)}
        sx={{ mt: 0, mb: 2, maxWidth: 320 }}
      />

      <ClientPaginatedTable rows={movements} resetKey={warehouseFilter}>
        {(pageRows) => (
          <GeneralTableBox fixedLayout minTableWidth={1040}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {COLUMNS.map((column) => (
                    <HeadTableCell key={column.key} align={column.align} sx={column.sx}>
                      {column.label}
                    </HeadTableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {isLoading ? (
                  <TableBodySkeleton />
                ) : movements.length === 0 ? (
                  <NoDataSkeleton colSpan={COLUMNS.length} message={TEXTS.empty} showSubtitle={false} />
                ) : (
                  pageRows.map((movement) => {
                    const warehouseLabel = getWarehouseLabel(warehousesByUuid, movement.uuid_warehouse);
                    const movementType = formatMovementType(movement.movement_type);
                    const reason = movement.observations || '-';
                    return (
                      <TableRow key={movement.uuid} hover>
                        <RowTableCell sx={textSx}>
                          {formatMovementDate(movement.created_at)}
                        </RowTableCell>
                        <RowTableCell sx={textSx} title={warehouseLabel}>
                          {warehouseLabel}
                        </RowTableCell>
                        <RowTableCell sx={textSx} title={movementType}>
                          {movementType}
                        </RowTableCell>
                        <RowTableCell align="right" sx={numberSx}>
                          {formatStockQuantity(movement.quantity)}
                        </RowTableCell>
                        <RowTableCell align="right" sx={numberSx}>
                          {formatStockQuantity(movement.previous_quantity)}
                        </RowTableCell>
                        <RowTableCell align="right" sx={numberSx}>
                          {formatStockQuantity(movement.new_quantity)}
                        </RowTableCell>
                        <RowTableCell sx={textSx} title={reason}>
                          {reason}
                        </RowTableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </GeneralTableBox>
        )}
      </ClientPaginatedTable>
    </>
  );
};

export default ArticleStockMovementsTable;
